import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useClaimBill } from '../billing/useClaimBill.js';

export function CertificationPage(): ReactNode {
  const { t } = useTranslation();
  const { money } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const { claimId } = useParams<{ claimId: string }>();
  const { token } = useAuth();
  const { bill, error: claimError } = useClaimBill(`/v1/approval/claim/${claimId}`);
  const [certs, setCerts] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!claimId) return;
    let alive = true;
    apiRequest<any[]>(`/v1/approval/certification/claim/${claimId}`, { token })
      .then(d => { if (alive) setCerts(Array.isArray(d) ? d : []); })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [claimId, token]);

  return (
    <div style={{ padding: 24 }}>
      <h1>{t('fa.cert.title', { bill })}</h1>
      {(error ?? claimError) && <p style={{ color: 'red' }}>{error ?? claimError}</p>}
      {/* The certificate is issued by the finance approval itself, at the
          bill's own figures; this page shows what was issued. */}
      {certs.length === 0 && <p>{t('fa.cert.noCert')}</p>}
      {certs.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{[t('fa.cert.hdrCertificateNo'), t('fa.cert.hdrType'), t('fa.cert.hdrAmount'), t('fa.cert.hdrTax'), t('fa.cert.hdrTotal'), t('fa.cert.hdrStatus'), t('fa.cert.hdrIssued')].map(h => <th key={h} style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid var(--color-border-default)' }}>{h}</th>)}</tr></thead>
          <tbody>{certs.map(c => (
            <tr key={c.certificationId}>
              <td style={{ padding: 8 }}>{c.certificateNumber}</td>
              <td style={{ padding: 8 }}>{c.certificateType}</td>
              <td style={{ padding: 8 }}>₹{money(Number(c.certifiedAmount))}</td>
              <td style={{ padding: 8 }}>₹{money(Number(c.certifiedTaxAmount))}</td>
              <td style={{ padding: 8, fontWeight: 700 }}>₹{money(Number(c.certifiedTotalAmount))}</td>
              <td style={{ padding: 8 }}>{c.certificateStatus ?? 'ISSUED'}</td>
              <td style={{ padding: 8 }}>{c.certifiedAt ? atDay(c.certifiedAt) : '—'}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}
